/**
 * Daily metrics: the numbers a day holds that nobody entered, per
 * `/specs/features/weighin.spec.md`. Steps are the one there is.
 *
 * Web reads them and never writes them — Health Connect lives on the phone, and this app has
 * no equivalent to read. An account that has never opened the Android app simply has no days
 * here, which is a metrics row without a steps card rather than anything to explain.
 */

/** The kinds a day can hold. The server keeps the same list, and refuses anything else. */
export const METRIC_KINDS = ['steps'] as const

export type MetricKind = (typeof METRIC_KINDS)[number]

export const METRIC_LABELS: Record<MetricKind, string> = {
  steps: 'Steps',
}

/**
 * One day of one kind, exactly as the server keeps it.
 *
 * The kind is a bare string rather than [MetricKind] because the server keeps an open list: a
 * kind a build does not know is a row it hands over unchanged, rather than a decode that fails
 * and takes the whole answer with it.
 */
export interface DailyMetric {
  day: string
  kind: string
  value: number
}

/** How the count reads: grouped, because six digits unbroken is not a number anybody scans. */
export function formatMetric(kind: string, value: number): string {
  return kind === 'steps' ? Math.round(value).toLocaleString() : String(value)
}

/** What a day held of one kind, or null where it held none. */
export function metricOn(
  metrics: readonly DailyMetric[],
  day: string,
  kind: string,
): number | null {
  return metrics.find((metric) => metric.day === day && metric.kind === kind)?.value ?? null
}
